import { Download } from '@mui/icons-material';
import { Button, Link, Stack, Typography } from '@mui/material';
import { concat, get } from 'lodash';
import React, { FC } from 'react';
import CreateForm from 'src/components/ui-component/form-component/create-form/CreateForm';
import CreateTable from 'src/components/ui-component/form-component/create-form/CreateTable';
import { useApplication } from 'src/context/ApplicationContext';
import { ALS_ERLEDIGT_MARKIEREN_LABEL, EIGENTUM_DER_LIEGENSCHAFT, FORM_FIELDS_TYPES } from 'src/lib/constants/constant';
import { PUBLIC_DOCUMENTS_PATHS } from 'src/lib/constants/publicDocumentPaths';
import { FormStrucItem } from 'src/lib/types/types';
import { ForceTrue, NonRequired } from 'src/lib/validation/ValidationTypes';
import PruefAssistenz from 'src/pages/private/online-antrag/antrag-components/PruefAssistenz';
import { InhaltTrenner, InhaltWrapper } from 'src/pages/private/online-antrag/antrag-components/Wrapper';
import ApplicationHeading from 'src/pages/private/online-antrag/application-heading/ApplicationHeading';
import { checkIfPruefAssitenzNeedsToValidate } from 'src/pages/private/online-antrag/application-helper-function/AppHelperfunctions';
import { checkReadOnlyInUeberarbeitung, handleDownloadFile } from 'src/utils/helper';

// import schemas
import AntragSchema from 'src/lib/paths/antrag_FULL_Paths.json';
const P = AntragSchema.attributes;

const FoerdervoraussetzungenEmobLis: FC<{ hasComment?: boolean }> = ({ hasComment }) => {
	const { application, setApplicationProperty, readOnly } = useApplication();
	const updatedReadOnly = checkReadOnlyInUeberarbeitung(application, readOnly, hasComment);

	return (
		<>
			<ApplicationHeading title='Fördervoraussetzungen' description={''} />

			<PruefAssistenz
				statusPath={
					P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Projektbeschreibung_Elek_0148514099_tzungen_Pruefung_Pruefstatus.path
				}
				hinweisPath={
					P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Projektbeschreibung_Elek_0128586498_nweis_Fuer_Den_Antragsteller.path
				}
				ueberarbeitetAmPath={
					P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Projektbeschreibung_Elek_0126303357_en_Pruefung_Ueberarbeitet_Am.path
				}
				erledigtPath={
					P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Projektbeschreibung_Elek_0209715028_ssetzungen_Pruefung_Erledigt.path
				}
				formStruc={formStrucPruefAssistenz()}
			/>

			<Typography>
				Ziel dieses Förderaufrufs ist es, noch in diesem Jahr die Entwicklung und den Ausbau von Schnellladepunkten in ländlichen Regionen zu
				unterstützen. Die Beantragung der Förderung im Rahmen des “LadeinfrastrukturSprint 2025” ist daher an die unten genannten Voraussetzungen
				gebunden.{' '}
			</Typography>

			<Typography sx={{ fontWeight: 500, mt: '1rem' }}>Bitte bestätigen Sie die für Ihr Projekt zutreffenden Punkte:</Typography>

			<InhaltWrapper>
				<Typography variant='h4' component={'h2'}>
					Fördergebiet
				</Typography>
				<Typography>
					Die Förderung beschränkt sich auf die Errichtung von öffentlich zugänglicher Ladeinfrastruktur in ländlich geprägten Räumen in Hessen.
					Das entsprechende Fördergebiet kann der Karte “Zentrale Orte und Strukturräume in Hessen” entnommen werden (Karte: siehe unter
					"Allgemeine Hinweise / Downloads" oder über diesen Link:{' '}
					<strong>
						{' '}
						<Link target='_blank' href='https://www.innovationsfoerderung-hessen.de/mm/Karte_Strukturraeume_Hessen.pdf ' download>
							KLICK
						</Link>
					</strong>
					)
				</Typography>
			</InhaltWrapper>

			<InhaltWrapper>
				<CreateForm formStruc={formStruc()} formValues={application} updateFormValuesFunc={setApplicationProperty} readOnly={updatedReadOnly} />
			</InhaltWrapper>

			<InhaltWrapper>
				<InhaltTrenner />
				<Typography variant='h4' component={'h2'}>
					Ladeinfrastruktur
				</Typography>
				<CreateForm
					formStruc={formStrucLadeInfrastruktur()}
					formValues={application}
					updateFormValuesFunc={setApplicationProperty}
					readOnly={updatedReadOnly}
				/>
			</InhaltWrapper>

			<InhaltWrapper>
				<InhaltTrenner />
				<Typography variant='h4' component={'h2'}>
					Projektplanung & Umsetzung
				</Typography>
				<CreateForm
					formStruc={formStrucProjektPlanung()}
					formValues={application}
					updateFormValuesFunc={setApplicationProperty}
					readOnly={updatedReadOnly}
				/>
			</InhaltWrapper>

			<InhaltWrapper>
				<InhaltTrenner />
				<Typography variant='h4' component={'h2'}>
					Eigentum der Liegenschaft
				</Typography>
				<CreateForm
					formStruc={formStrucEigentum()}
					formValues={application}
					updateFormValuesFunc={setApplicationProperty}
					readOnly={updatedReadOnly}
				/>
			</InhaltWrapper>

			{get(application, P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Eigentum_Der_Liegenschaft.path) ===
				EIGENTUM_DER_LIEGENSCHAFT.DerAntragstellerVerfuegt_ZuBetreiben && (
				<InhaltWrapper>
					<Typography sx={{ fontWeight: 500 }}>Falls dieser Punkt auf Sie zutrifft: </Typography>

					<Stack spacing={3} sx={{ width: '100%', mt: '1rem', mb: '1rem' }}>
						<Typography>
							Sie können die folgende Vorlage zur Einholung der „Genehmigung durch den Eigentümer“ nutzen oder einen anderen Nachweis zur
							Nutzungsgenehmigung über die vorgeschriebene Zweckbindungsfrist von 5 Jahren hochladen.
						</Typography>
						<Button
							variant='outlined'
							startIcon={<Download />}
							onClick={() => handleDownloadFile(PUBLIC_DOCUMENTS_PATHS.antragsUnterlagen.genehmigungEigentuemerLIS)}
							sx={{ maxWidth: 'fit-content' }}
						>
							Genehmigung Eigentümer herunterladen
						</Button>
						<Typography>
							Laden Sie bitte anschließend die unterzeichnete Genehmigung des Eigentümers als pdf-Dokument hier wieder hoch:
						</Typography>
					</Stack>
					<CreateTable
						formStruc={formStrucUpload(application)}
						formValues={application}
						updateFormValuesFunc={setApplicationProperty}
						readOnly={updatedReadOnly}
						columnsLabel={['Bezeichnungs des Dokuments', 'Datei']}
					/>
				</InhaltWrapper>
			)}
		</>
	);
};

export default FoerdervoraussetzungenEmobLis;

const formStrucPruefAssistenz = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Projektbeschreibung_Elek_0209715028_ssetzungen_Pruefung_Erledigt,
					label: ALS_ERLEDIGT_MARKIEREN_LABEL,
					validation: [{ validateFunction: ForceTrue }],
				},
			],
		},
	];
	return struc;
};

const formStruc = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Foerdergebiet_Errichtet,
					// label:
					type: FORM_FIELDS_TYPES.radio,
					direction: 'column',
					validation: [{ validateFunction: NonRequired }],
				},
			],
			userProps: { direction: 'column', columnToDivide: 2 },
		},
	];
	return struc;
};
const formStrucLadeInfrastruktur = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Ladeinfrastruktur_Uneingeschraekt_Zugaenglich,
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Schnellladepunkte_Ladeleistung,
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Ladeinfrastruktur_Fuenf_Jahre_Betreiben,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucProjektPlanung = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Vorhaben_Noch_Nicht_Begonnen,
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Projektbezogenen_Gesamtausgaben,
				P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Ladeinfrastruktur_In_Betrieb_Genommen,
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};
const formStrucEigentum = () => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{
					...P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Eigentum_Der_Liegenschaft,
					type: FORM_FIELDS_TYPES.radio,
					direction: 'column',
					validation: [{ validateFunction: NonRequired }],
				},
			],
			userProps: { direction: 'column', columnToDivide: 1 },
		},
	];
	return struc;
};

const formStrucUpload = (application: any) => {
	let struc: FormStrucItem[] = [
		{
			fields: [
				{ path: 't.t', type: 'label', label: 'Genehmigung des Eigentümers', validation: [{ validateFunction: NonRequired }] },
				{
					...P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Genehmigung_Eigentuemer,
					hideLabel: true,
				},
			],
		},
	];

	if (
		get(application, P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Eigentum_Der_Liegenschaft.path) ===
		EIGENTUM_DER_LIEGENSCHAFT.DerAntragstellerVerfuegt_ZuBetreiben
	) {
		return struc;
	} else {
		return [];
	}
};

export const getFormStrucFoerdervoraussetzungenEmobLis = (application: any) => {
	if (
		checkIfPruefAssitenzNeedsToValidate(
			application,
			P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Projektbeschreibung_Elek_0128586498_nweis_Fuer_Den_Antragsteller.path,
			P.Projektbeschreibung_Elektromobilitaet_LIS.schema.attributes.Projektbeschreibung_Elek_0209715028_ssetzungen_Pruefung_Erledigt.path,
		)
	) {
		return concat(formStrucPruefAssistenz(), formStruc(), formStrucUpload(application));
	} else return concat(formStruc(), formStrucUpload(application));
};
